import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/sonner";
import { CalendarClock, Loader2, MessageSquarePlus, StickyNote } from "lucide-react";

const today = () => new Date(Date.now() + 5.5 * 3600000).toISOString().slice(0, 10);

export const FollowUpBadge = ({ date, t }) => {
  if (!date) return null;
  const overdue = date <= today();
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${overdue ? "bg-rose-100 text-rose-700" : "bg-sky-50 text-sky-700"}`} data-testid="funnel-lead-followup-badge">
      <CalendarClock className="h-3.5 w-3.5" /> {t(overdue ? "admin.followUpDue" : "admin.followUpOn", { date })}
    </span>
  );
};

export const LeadNotes = ({ lead, t, onChange }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(lead.follow_up_date || "");
  useEffect(() => {
    if (!date || date === (lead.follow_up_date || "")) return undefined;
    const id = setTimeout(() => setFollowUp(date), 900);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);
  const notes = lead.notes || [];
  const addNote = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/funnel-leads/${lead.lead_id}/notes`, { text });
      onChange({ notes: [...notes, data] });
      setText("");
      toast.success(t("admin.noteAdded"));
    } catch {
      toast.error(t("funnel.common.error"));
    } finally {
      setBusy(false);
    }
  };
  const setFollowUp = async (v) => {
    if (v === (lead.follow_up_date || "")) return;
    if (v && !(/^\d{4}-\d{2}-\d{2}$/.test(v) && +v.slice(0, 4) >= 2020 && +v.slice(0, 4) <= 2100)) return;
    try {
      await api.patch(`/admin/funnel-leads/${lead.lead_id}`, { follow_up_date: v });
      setDate(v);
      onChange({ follow_up_date: v });
      toast.success(t("admin.followUpSaved"));
    } catch {
      setDate(lead.follow_up_date || "");
      toast.error(t("funnel.common.error"));
    }
  };
  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      <div className="flex flex-wrap items-center gap-3">
        <Button size="sm" variant="ghost" onClick={() => setOpen(!open)} data-testid="funnel-lead-notes-toggle">
          <StickyNote className="mr-1.5 h-4 w-4" /> {t("admin.notes", { count: notes.length })}
        </Button>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          {t("admin.followUp")}
          <Input type="date" min="2020-01-01" max="2100-12-31" value={date} onChange={(e) => setDate(e.target.value)} className="h-8 w-40" data-testid="funnel-lead-followup" />
        </label>
        {lead.follow_up_date && <Button size="sm" variant="ghost" className="text-slate-500" onClick={() => setFollowUp("")} data-testid="funnel-lead-followup-clear">{t("admin.followUpClear")}</Button>}
      </div>
      {open && (
        <div className="mt-3 space-y-2" data-testid="funnel-lead-notes">
          {notes.map((n) => (
            <div key={n.note_id} className="rounded-lg bg-amber-50/60 px-3 py-2 text-sm" data-testid="funnel-lead-note">
              <p className="whitespace-pre-wrap text-slate-800">{n.text}</p>
              <p className="mt-1 text-xs text-slate-500">{n.by} · {new Date(n.at).toLocaleString()}</p>
            </div>
          ))}
          <div className="flex gap-2">
            <Textarea rows={2} maxLength={1000} value={text} onChange={(e) => setText(e.target.value)} placeholder={t("admin.notePlaceholder")} data-testid="funnel-lead-note-input" />
            <Button onClick={addNote} disabled={busy || !text.trim()} className="self-end bg-amber-600 hover:bg-amber-700" data-testid="funnel-lead-note-add">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquarePlus className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
